import React, { useEffect } from 'react';
import { Box, Button, Flex, Icon, Input, Spinner, Text } from '@chakra-ui/react';
import { useSelector, useDispatch } from 'react-redux';
import { openDialog, closeDialog } from '../../store/dialogSlice';
import { loadQrHistoryRequest, loadQrCountsRequest, downloadQrCodesRequest, downloadCsvRequest, generateQrCodesRequest, stopPollingRequest, continuePollingRequest, clearCurrentJob, softHideQrRunRequest } from '../../store/qrSlice';
import { loadModelsRequest } from '../../store/modelsSlice';
import { useForm, Controller } from 'react-hook-form';
import { Header } from '../../components/shared/IndexHeader';
import FitTabs from '../../components/shared/tabs/Tabs';
import TableContainer from '../../components/shared/table/TableContainer';
import PaginationComponent from '../../components/shared/panel/Pagination';
import { useColorModeValue } from '../../components/ui/color-mode';
import SelectBox from '../../components/shared/fields/SelectBox';
import ButtonBox from '../../components/shared/buttons/ButtonBox';
import { showAlert } from '../../store/alertSlice';
import { darkMode, lightMode } from '../../theme/config';
import DialogBox from '../../components/shared/dialogs/DialogBox';
import { LuHistory, LuQrCode, LuRefreshCw } from 'react-icons/lu';
import { MdAdd } from 'react-icons/md';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { dateFormatter } from '../../utils/dateTime/date';
import ExportButton from '../../components/shared/buttons/ExportButton';
import QrCountCard from '../../components/shared/cards/QrCountCard';
import DatePicker from 'react-datepicker';
import { FaCalendarAlt } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import { IoMdEye } from 'react-icons/io';

const QRCodeGeneration = () => {
  const borderColor = useColorModeValue(lightMode.containerBorderColor, darkMode.containerBorderColor);
  const textColor = useColorModeValue(lightMode.textColor, darkMode.textColor);
  const backgroundColor = useColorModeValue(lightMode.boxBackgroundColor, darkMode.boxBackgroundColor);

  // Job status colors
  const jobStatusBg = useColorModeValue('blue.50', 'blue.900');
  const jobStatusBorderColor = useColorModeValue('blue.200', 'blue.700');
  const jobStatusTitleColor = useColorModeValue('blue.800', 'blue.200');

  const dispatch = useDispatch();
  const { models } = useSelector((state) => state.models || { models: [], loaded: false });
  const {
    history = [],
    loading: qrLoading,
    downloading: qrDownloading,
    generating: qrGenerating,
    polling: qrPolling,
    currentJob,
    nextToken,
    count,
    counts
  } = useSelector((state) => state.qr || {
    history: [],
    loading: false,
    downloading: false,
    generating: false,
    polling: false,
    currentJob: null,
    nextToken: null,
    count: 0,
    counts: { Total: 0, Used: 0, Hidden: 0, period: 'all time', loading: false, error: null }
  });

  const [activeTab, setActiveTab] = React.useState('generate');

  // Inline filter state for export
  const [exportFromDate, setExportFromDate] = React.useState(null);
  const [exportToDate, setExportToDate] = React.useState(null);
  const [exportModel, setExportModel] = React.useState(null);

  // State for hide dialog
  const [hideQrRunId, setHideQrRunId] = React.useState(null);
  const hideDialogOpen = useSelector((state) => state.dialogs.hideQrRun);

  // Pagination state
  const [currentPage, setCurrentPage] = React.useState(1);
  const [tokenStack, setTokenStack] = React.useState([0]);
  const [pageTokens, setPageTokens] = React.useState({ 1: 0 });
  const pageSize = 10;

  useEffect(() => {
    if (activeTab === 'generate') {
      dispatch(loadModelsRequest());
      dispatch(loadQrCountsRequest());
    }
  }, [activeTab, dispatch]);

  useEffect(() => {
    if (activeTab === 'history') {
      setCurrentPage(1);
      setTokenStack([0]);
      setPageTokens({ 1: 0 });
      dispatch(loadQrHistoryRequest({ limit: pageSize, nextToken: 0 }));
    }
  }, [activeTab, dispatch, pageSize]);

  useEffect(() => {
    if (activeTab === 'history' && !qrLoading && count > 0) {
      const maxValidPage = Math.ceil(count / pageSize);
      if (currentPage > maxValidPage) {
        console.warn(`Current page ${currentPage} exceeds max valid page ${maxValidPage}. Redirecting...`);
        handlePageChange(maxValidPage);
      }
    }
  }, [activeTab, qrLoading, count, currentPage, pageSize]);

  // Function to get export filters based on inline selections
  const getExportFilters = () => {
    const filters = {};

    if (exportFromDate) {
      filters.fromDate = exportFromDate.toLocaleDateString('en-CA');
    }
    if (exportToDate) {
      filters.toDate = exportToDate.toLocaleDateString('en-CA');
    }
    if (exportModel) {
      filters.category = exportModel;
    }

    return filters;
  };

  // Create model options for export filter from the models data
  const modelOptions = React.useMemo(() => {
    return (models || []).map(model => ({
      label: model.label,
      value: model.value
    }));
  }, [models]);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({
    defaultValues: {
      model: [],
      quantity: '',
      startSerialNo: '',
    }
  });
  const onSubmit = (values) => {
    const skuId = Array.isArray(values.model) ? values.model[0]?.value || values.model[0] : values.model;
    const quantity = Number(values.quantity) || 0;
    const qrCodeImageFormat = 'png';
    const startSerialNo = values.startSerialNo ? String(values.startSerialNo).trim() : '';

    // Validation: required fields
    if (!skuId) {
      dispatch(showAlert({ type: 'error', message: 'Please select a model (SKU) before generating QR codes.' }));
      return;
    }

    if (!quantity || quantity <= 0) {
      dispatch(showAlert({ type: 'error', message: 'Please enter a valid quantity greater than 0.' }));
      return;
    }

    if (!startSerialNo) {
      dispatch(showAlert({ type: 'error', message: 'Please provide a start serial number.' }));
      return;
    }

    const lastChar = startSerialNo.slice(-1);
    if (!/\d/.test(lastChar)) {
      dispatch(showAlert({ type: 'error', message: 'Start serial number must end with a number (0-9).' }));
      return;
    }

    dispatch(generateQrCodesRequest({ skuId, quantity, qrCodeImageFormat, startSerialNo }));

    reset();
  };
  const historyColumns = [
    { key: 'skuId', label: 'Model (SKU)', width: '22%' },
    { key: 'quantity', label: 'Quantity', width: '8%' },
    { key: 'format', label: 'Format', width: '8%', showInChip: true },
    { key: 'startSerialNo', label: 'Start Serial', width: '12%' },
    { key: 'createdByName', label: 'Created By', width: '16%' },
    { key: 'created_at', label: 'Created At', width: '14%' },
    { key: 'qrCodeDownload', label: 'QR Code', width: '10%' },
    { key: 'serialNoDownload', label: 'Serial No', width: '10%' },
    { key: 'hide', label: 'Hide', width: '8%' },
  ];

  const handleDownload = (runId, s3Link, startSerialNo) => {
    if (!runId || !s3Link) {
      dispatch(showAlert({ type: 'error', message: 'Download not available - missing run ID or link' }));
      return;
    }

    dispatch(downloadQrCodesRequest({ runId, s3Link, startSerialNo }));
  };

  const handleDownloadCsv = (submissionId, startSerialNo, createdDate) => {
    if (!submissionId) {
      dispatch(showAlert({ type: 'error', message: 'Download not available - missing submission ID' }));
      return;
    }

    dispatch(downloadCsvRequest({ submissionId, startSerialNo, createdDate }));
  };

  const handleSoftHide = (runId) => {
    if (!runId) {
      dispatch(showAlert({ type: 'error', message: 'Hide not available - missing run ID' }));
      return;
    }

    setHideQrRunId(runId);
    dispatch(openDialog('hideQrRun'));
  };

  const handleHideConfirm = () => {
    if (hideQrRunId) {
      dispatch(softHideQrRunRequest({ runId: hideQrRunId }));
      dispatch(closeDialog('hideQrRun'));
      setHideQrRunId(null);
    }
  };

  const handleHideCancel = () => {
    dispatch(closeDialog('hideQrRun'));
    setHideQrRunId(null);
  };

  const handleStopPolling = () => {
    dispatch(stopPollingRequest());
  };

  const handleContinuePolling = () => {
    dispatch(continuePollingRequest());
  };

  const handleClearJob = () => {
    dispatch(clearCurrentJob());
  };

  const estimatedTotalPages = count > 0 ? Math.ceil(count / pageSize) : 1;
  const totalPages = count > 0 ? estimatedTotalPages : (nextToken !== null ? currentPage + 1 : currentPage);

  const maxValidPage = count > 0 ? estimatedTotalPages : (nextToken !== null ? currentPage + 1 : currentPage);
  const availablePages = Array.from({ length: maxValidPage }, (_, i) => i + 1);

  const handlePageChange = (page) => {
    // Validate page number
    if (page < 1) {
      console.warn(`Invalid page ${page}. Redirecting to page 1.`);
      handlePageChange(1);
      return;
    }

    if (count > 0 && page > Math.ceil(count / pageSize)) {
      console.warn(`Invalid page ${page}. Valid range: 1 to ${Math.ceil(count / pageSize)}. Redirecting to last valid page.`);
      handlePageChange(Math.ceil(count / pageSize));
      return;
    }

    if (pageTokens[page] !== undefined) {
      // We have the offset for this page, navigate directly
      const offset = pageTokens[page];
      dispatch(loadQrHistoryRequest({ limit: pageSize, nextToken: offset }));
      setCurrentPage(page);

      // Update token stack to reflect current position
      const newStack = [];
      for (let i = 1; i <= page; i++) {
        if (pageTokens[i] !== undefined) {
          newStack.push(pageTokens[i]);
        }
      }
      setTokenStack(newStack);
    } else if (page === currentPage + 1 && nextToken !== null) {
      // Sequential navigation to next page
      handleNext();
    } else {
      // For offset-based pagination, we can calculate any page
      const offset = (page - 1) * pageSize;
      dispatch(loadQrHistoryRequest({ limit: pageSize, nextToken: offset }));
      setCurrentPage(page);
      setPageTokens(prev => ({ ...prev, [page]: offset }));

      // Update token stack
      const newStack = [];
      for (let i = 1; i <= page; i++) {
        const pageOffset = (i - 1) * pageSize;
        newStack.push(pageOffset);
        setPageTokens(prev => ({ ...prev, [i]: pageOffset }));
      }
      setTokenStack(newStack);
    }
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      const offset = (newPage - 1) * pageSize;

      setCurrentPage(newPage);
      dispatch(loadQrHistoryRequest({ limit: pageSize, nextToken: offset }));

      // Update token stack
      const newStack = tokenStack.slice(0, -1);
      setTokenStack(newStack);
    }
  };

  const handleNext = () => {
    const newPage = currentPage + 1;

    // Prevent going beyond max valid page if we know the total count
    if (count > 0 && newPage > Math.ceil(count / pageSize)) {
      console.warn(`Cannot go to page ${newPage}. Maximum valid page is ${Math.ceil(count / pageSize)}.`);
      return;
    }

    if (nextToken !== null) {
      const offset = nextToken;
      setPageTokens(prev => ({ ...prev, [newPage]: offset }));
      setTokenStack(stack => [...stack, offset]);
      dispatch(loadQrHistoryRequest({ limit: pageSize, nextToken: offset }));
      setCurrentPage(newPage);
    }
  };

  const historyRows = (history || []).map((it, idx) => {
    const runId = it.runId || it.runid;
    const s3Link = it.s3Link || it.qrCodeS3Link || it.downloadURL || '';
    const startSerialNo = it.startSerialNo || '';

    // Match skuId with models to get the model name
    const modelData = models.find(model => model.value === it.skuId);
    const modelName = modelData?.label || it.skuId || '-';

    // Check if created_at is more than 1 year old
    const createdAt = new Date(it.created_at);
    const now = new Date();
    const daysDifference = (now - createdAt) / (1000 * 60 * 60 * 24);
    const isExpired = daysDifference > 365;

    const qrDownloadButton = isExpired ? (
      <ButtonBox
        size="sm"
        colorPalette="red"
        variant="outline"
        name="Expired"
        disabled={true}
      />
    ) : (
      <ButtonBox
        size="sm"
        colorPalette="green"
        onClick={(e) => {
          e.stopPropagation();
          handleDownload(runId, s3Link, startSerialNo);
        }}
        name="Download"
        disabled={qrDownloading}
        icon={<Icon as={LuQrCode} boxSize={4} />}
      />
    );

    const serialDownloadButton = isExpired ? (
      <ButtonBox
        size="sm"
        colorPalette="red"
        variant="outline"
        name="Expired"
        disabled={true}
      />
    ) : (
      <ButtonBox
        size="sm"
        colorPalette="green"
        onClick={(e) => {
          e.stopPropagation();
          handleDownloadCsv(runId, startSerialNo, it.created_at);
        }}
        name="Download"
        disabled={qrDownloading}
        icon={<Icon as={RiFileExcel2Fill} boxSize={4} />}
      />
    );

    const deleteButton = (
      <ButtonBox
        size="sm"
        colorPalette="red"
        onClick={(e) => {
          e.stopPropagation();
          handleSoftHide(runId);
        }}
        name="Hide"
        disabled={qrLoading}
        icon={<Icon as={IoMdEye} boxSize={4} />}
      />
    );

    return {
      id: it.runId || it.runid || idx,
      skuId: modelName,
      quantity: it.quantity ?? '-',
      format: it.format.toUpperCase() || '-',
      startSerialNo: it.startSerialNo || '-',
      createdByName: it.createdByName || it.createdBy || '-',
      created_at: dateFormatter(it.created_at) || '-',
      qrCodeDownload: qrDownloadButton,
      serialNoDownload: serialDownloadButton,
      hide: deleteButton,
    };
  });

  const tabs = [
    {
      value: 'generate',
      label: <><Icon as={LuQrCode} boxSize={4} /> Generate QR Codes</>,
      panel: (
        <Box>
          <Flex gap={4} mb={6} wrap="wrap">
            <QrCountCard
              count={counts?.Total}
              label="Total QR Codes Generated"
              bgColor={{ light: useColorModeValue('blue.100', 'blue.700'), dark: useColorModeValue('blue.100', 'blue.700') }}
              iconColor={{ light: useColorModeValue('blue.600', 'blue.200'), dark: useColorModeValue('blue.600', 'blue.200') }}
            />
            <QrCountCard
              count={counts?.Used}
              label="QR Codes Used"
              bgColor={{ light: useColorModeValue('green.100', 'green.700'), dark: useColorModeValue('green.100', 'green.700') }}
              iconColor={{ light: useColorModeValue('green.600', 'green.200'), dark: useColorModeValue('green.600', 'green.200') }}
            />
            <QrCountCard
              count={counts?.Hidden}
              label="QR Codes Hidden"
              bgColor={{ light: useColorModeValue('red.100', 'red.700'), dark: useColorModeValue('red.100', 'red.700') }}
              iconColor={{ light: useColorModeValue('red.600', 'red.200'), dark: useColorModeValue('red.600', 'red.200') }}
            />
          </Flex>
          <Header
            title="Generate QR Codes"
            subtitle={
              <>
                <Text>Choose a model and quantity to generate QR codes with serial numbers.</Text>
                <Text fontSize="sm" color="red.500">You may close this page once generation begins and check the History tab later.</Text>
                <br />
              </>
            }
            textColor={textColor}
            fontSize={22}
          />
          <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
            <Flex gap={4} my={4} align="flex-start" direction={{ base: 'column', md: 'row' }}>
              <Box w={{ base: '100%', md: '250px' }} display="flex" flexDirection="column">
                <SelectBox
                  items={models || []}
                  name="model"
                  control={control}
                  rules={{ required: "Model is required" }}
                  placeholder="Choose a model"
                  size="sm"
                />
                {errors.model && (
                  <Text color="red.500" fontSize="sm" mt={-5}>
                    {errors.model.message}
                  </Text>
                )}
              </Box>
              <Box maxW="200px" display="flex" flexDirection="column">
                <Controller
                  name="quantity"
                  control={control}
                  rules={{
                    required: "Quantity is required",
                    min: { value: 1, message: "Quantity must be greater than 0" }
                  }}
                  render={({ field }) => (
                    <Input {...field} placeholder="Enter quantity" type="number" />
                  )}
                />
                {errors.quantity && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.quantity.message}
                  </Text>
                )}
              </Box>
              <Box maxW="240px" display="flex" flexDirection="column">
                <Controller
                  name="startSerialNo"
                  control={control}
                  rules={{
                    required: "Start serial number is required",
                    validate: (value) => {
                      if (!value) return "Start serial number is required";
                      const lastChar = value.slice(-1);
                      return /\d/.test(lastChar) || "Must end with a number (0-9)";
                    }
                  }}
                  render={({ field, fieldState }) => (
                    <Input {...field} placeholder="Start Serial No" type="text" />
                  )}
                />
                {errors.startSerialNo && (
                  <Text color="red.500" fontSize="sm" mt={1}>
                    {errors.startSerialNo.message}
                  </Text>
                )}
              </Box>
              <Box display="flex" border="1px solid" borderColor={borderColor} p={1} borderRadius="sm" alignItems="center" height="fit-content">
                <Text ml={2}>Format:</Text>
                <Box px={3} py={1} color="blue.500" borderRadius="md" fontSize="sm">PNG</Box>
              </Box>
              <Box alignSelf="flex-start">
                {qrGenerating || qrPolling ? (
                  <>
                    <Button
                      fontWeight="600"
                      disabled={qrLoading || qrGenerating || qrPolling}
                      bg="blue.500"
                      color="white"
                    >
                      <Spinner size="sm" color="white" mr={2} />
                      <Text>Generating...</Text>
                    </Button>
                  </>
                ) : (
                  <Button
                    fontWeight="700"
                    type="submit"
                    disabled={qrLoading || qrGenerating || qrPolling}
                    bg="blue.500"
                    color="white"
                  >
                    <MdAdd size="sm" color="white" mr={2} />
                    <Text>Generate</Text>
                  </Button>
                )}
              </Box>
            </Flex>
          </form>

          {/* Job Status Display */}
          {currentJob && (
            <Box mt={4} p={4} bg={jobStatusBg} borderRadius="md" border="1px solid" borderColor={jobStatusBorderColor}>
              <Flex justify="space-between" align="center">
                <Box>
                  <Box fontWeight="bold" color={jobStatusTitleColor}>
                    QR Generation Status: {currentJob.status}
                  </Box>
                </Box>
                <Flex gap={2}>
                  {qrPolling && (
                    <ButtonBox size="sm" onClick={handleStopPolling} variant="outline" name="Stop Checking" />
                  )}
                  {!qrPolling && currentJob.status === 'SUBMITTED' && (
                    <ButtonBox size="sm" onClick={handleContinuePolling} colorPalette="blue" name="Continue Checking" />
                  )}
                  {currentJob.status === 'COMPLETED' && currentJob.s3Link && (
                    <ButtonBox
                      size="sm"
                      onClick={() => {
                        // Use startSerialNo from currentJob, or find matching history item, or fallback to first history item
                        let serialNo = currentJob.startSerialNo;

                        if (!serialNo) {

                          if (history.length > 0) {
                            // Try to find matching history item by submissionId/runId
                            const matchingItem = history.find(item =>
                              item.runId === currentJob.submissionId ||
                              item.runid === currentJob.submissionId
                            );
                            serialNo = matchingItem ? matchingItem.startSerialNo : history[0].startSerialNo;
                          }
                        }

                        handleDownload(currentJob.submissionId, currentJob.s3Link, serialNo);
                      }}
                      colorPalette="green"
                      name="Download Results"
                    />
                  )}
                  <ButtonBox size="sm" onClick={handleClearJob} variant="ghost" name="Clear" />
                </Flex>
              </Flex>
            </Box>
          )}
        </Box>
      )
    },
    {
      value: 'history',
      label: <><Icon as={LuHistory} boxSize={4} /> QR History</>,
      panel: (
        <Box>
          <Flex justify="space-between" align="center" mb={2}>
            <Header
              title="QR Code Generation History"
              subtitle={
                <>
                  <Text>View and download previously generated QR codes</Text>
                  <Text fontSize="sm" color="red.500">Downloads are available within the last 1 year of generation.</Text>
                  <br />
                </>
              }
              textColor={textColor}
              fontSize={22}
            />

            <Flex gap={2} wrap="wrap" align="center">
              {/* Inline Filters */}
              <Box maxW="150px" mt='-6'>
                <Text mb={1} fontSize="sm" fontWeight="semibold">
                  From Date
                </Text>
                <Box position="relative">
                  <Icon
                    as={FaCalendarAlt}
                    position="absolute"
                    left={2}
                    top="50%"
                    transform="translateY(-50%)"
                    color="gray.400"
                    pointerEvents="none"
                    zIndex={10}
                  />
                  <DatePicker
                    placeholderText="From date"
                    selected={exportFromDate}
                    onChange={(date) => setExportFromDate(date)}
                    customInput={<Input pl="2rem" bg={backgroundColor} size="sm" />}
                    maxDate={new Date()}
                    selectsStart
                    startDate={exportFromDate}
                    endDate={exportToDate}
                  />
                </Box>
              </Box>

              <Box maxW="150px" mt="-6">
                <Text mb={1} fontSize="sm" fontWeight="semibold">
                  To Date
                </Text>
                <Box position="relative">
                  <Icon
                    as={FaCalendarAlt}
                    position="absolute"
                    left={2}
                    top="50%"
                    transform="translateY(-50%)"
                    color="gray.400"
                    pointerEvents="none"
                    zIndex={10}
                  />
                  <DatePicker
                    placeholderText="To date"
                    selected={exportToDate}
                    onChange={(date) => setExportToDate(date)}
                    customInput={<Input pl="2rem" bg={backgroundColor} size="sm" />}
                    maxDate={new Date()}
                    selectsEnd
                    startDate={exportFromDate}
                    endDate={exportToDate}
                    minDate={exportFromDate}
                  />
                </Box>
              </Box>

              <Box maxW="200px">
                <Text mb={1} fontSize="sm" fontWeight="semibold">
                  Model
                </Text>
                <Box w={{ md: '150px' }} display="flex" flexDirection="column">
                  <SelectBox
                    items={[{ label: 'All Models', value: '' }, ...modelOptions]}
                    value={exportModel || ''}
                    onChange={(e) => setExportModel(e.target.value || null)}
                    placeholder="All Models"
                    size="sm"
                  />
                </Box>
              </Box>

              <ExportButton
                source="QRCODE"
                filters={getExportFilters()}
                fileType="text/csv"
                buttonLabel="Export"
                buttonProps={{ colorScheme: 'blue', variant: 'outline' }}
                backgroundColor={backgroundColor}
                disabled={qrLoading || historyRows.length === 0}
              />
              <Button
                fontWeight="600"
                size="sm"
                onClick={() => {
                  // Reset pagination state when refreshing
                  setCurrentPage(1);
                  setTokenStack([0]);
                  setPageTokens({ 1: 0 });
                  dispatch(loadQrHistoryRequest({ limit: pageSize, nextToken: 0 }));
                }}
                disabled={qrLoading}
                bg="blue.500"
                color="white"
                _hover={{ bg: 'blue.600' }}
                _active={{ bg: 'blue.700' }}
                _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
              >
                {qrLoading ? (
                  <>
                    <Spinner size="sm" mr={2} />
                    <Text>Loading...</Text>
                  </>
                ) : (
                  <>
                    <Icon as={LuRefreshCw} boxSize={4} mr={2} />
                    <Text>Refresh</Text>
                  </>
                )}
              </Button>
            </Flex>
          </Flex>

          <TableContainer columns={historyColumns} rows={historyRows} showColumnBorder isLoading={qrLoading} />

          {/* Show message when no data on current page */}
          {!qrLoading && historyRows.length === 0 && currentPage > 1 && (
            <Box textAlign="center" py={4}>
              <Text color="gray.500">No data found on page {currentPage}. Try navigating to a different page.</Text>
            </Box>
          )}

          {/* Pagination Component - Always show if we have total pages > 1 or if we're not on page 1 */}
          {(totalPages > 1 || currentPage > 1) && (
            <Flex justify="center" mt={4}>
              <PaginationComponent
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPrev={handlePrev}
                onNext={handleNext}
                availablePages={availablePages}
                hasNextToken={!!nextToken}
                size="sm"
                colorScheme="blue"
              />
            </Flex>
          )}
        </Box>
      )
    }
  ];

  const handleTabChange = (newVal) => {
    setActiveTab(newVal.value);
    if (newVal.value === 'history') {
      // Reset pagination state when switching to history tab
      setCurrentPage(1);
      setTokenStack([0]);
      setPageTokens({ 1: 0 });
      dispatch(loadQrHistoryRequest({ limit: pageSize, nextToken: 0 }));
    }
  };

  return (
    <Box border={{ base: '', md: '1px solid' }} borderColor={{ base: '', md: borderColor }} p={{ base: 3, md: 6 }} borderRadius="sm">
      <Header title="QR Code Management" subtitle="Generate and manage QR codes for your products" textColor={textColor} mb={4} />

      <FitTabs tabs={tabs} value={activeTab} onChange={handleTabChange} variant="enclosed" width="100%" fitted />

      {/* Hide QR Run Dialog */}
      <DialogBox
        dialog_title="Hide QR Generation Run"
        dialog_body="Are you sure you want to hide this QR generation run? This action cannot be undone."
        cancelButtonName="Cancel"
        SaveButtonName="Hide"
        saveButtonOnclick={handleHideConfirm}
        open={hideDialogOpen}
        onClose={handleHideCancel}
        saveButtonColor="red"
      />
    </Box>
  );
};

export default QRCodeGeneration;
